
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Bot } from '@/types/trader';
import { toggleAccountConnection, deleteBotAccount } from '@/services/bot-account-service';
import { useState } from 'react';

export const useBotActions = (botId: string | undefined) => {
  const queryClient = useQueryClient();
  const [accountActionError, setAccountActionError] = useState<any>(null);

  const updateBotStatus = useMutation({
    mutationFn: async (bot: Bot | null | undefined) => {
      if (!bot) throw new Error("No bot provided");
      
      const { error } = await supabase
        .from('bots')
        .update({ status: bot.status === 'active' ? 'inactive' : 'active' })
        .eq('id', bot.id);
        
      if (error) throw error;
      
      return { ...bot, status: bot.status === 'active' ? 'inactive' : 'active' };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bot', botId] });
      queryClient.invalidateQueries({ queryKey: ['bots'] });
    },
  });
  
  const deleteBotMutation = useMutation({
    mutationFn: async (bot: Bot | null | undefined) => {
      if (!bot) throw new Error("No bot provided");
      
      const { error } = await supabase
        .from('bots')
        .update({ is_deleted: true, deleted_at: new Date().toISOString() })
        .eq('id', bot.id);
        
      if (error) throw error;
      
      return bot;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots'] });
    },
  });
  
  const toggleAccountConnectionMutation = useMutation({
    mutationFn: async ({ accountId, isConnected }: { accountId: string; isConnected: boolean }) => {
      // Reset previous errors
      setAccountActionError(null);
      return toggleAccountConnection(accountId, isConnected);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['detailedBotAccounts', botId] });
    },
    onError: (error: any, variables) => {
      console.error('Error toggling account connection:', error);
      // Store error with accountId for reference
      setAccountActionError({
        ...error,
        accountId: variables.accountId
      });
    }
  });
  
  const deleteAccountMutation = useMutation({
    mutationFn: async (accountId: string) => {
      // Reset previous errors
      setAccountActionError(null);
      return deleteBotAccount(accountId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['detailedBotAccounts', botId] });
    },
    onError: (error: any, accountId) => {
      console.error('Error deleting account:', error);
      // Store error with accountId for reference
      setAccountActionError({
        ...error,
        accountId
      });
    }
  });
  
  return {
    toggleBotStatus: (bot: Bot | null | undefined) => updateBotStatus.mutate(bot),
    isUpdating: updateBotStatus.isPending,
    deleteBot: (bot: Bot | null | undefined) => deleteBotMutation.mutate(bot),
    isDeleting: deleteBotMutation.isPending,
    onToggleAccountConnection: (accountId: string, isConnected: boolean) => 
      toggleAccountConnectionMutation.mutate({ accountId, isConnected }),
    onDeleteAccount: (accountId: string) => deleteAccountMutation.mutate(accountId),
    accountActionError
  };
};
